import type { IconGlyph } from '@sunwebgroup/ui-components'
import { useIntl } from '@/intl'
import { HighlightedPaymentMethod } from '@/scopes/prices/components/HighlightedPaymentMethod'
import { BookNowButton } from '@/scopes/prices/components/PriceBasketLegacy/BookNowButton'
import { transportResponseSelector } from '@/scopes/prices/components/selectors'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import {
  Banner,
  Collapsible,
  CollapsibleRoot,
  CollapsibleTrigger,
  Icon,
  SpaceBetween,
  tk,
  Typography,
} from '@sunwebgroup/ui-components'
import * as React from 'react'
import { useMemo } from 'react'
import { css, styled } from 'styled-components'

const PACKAGE_USP: Record<string, IconGlyph> = {
  Discount: 'IconOptionalFacilities',
  FreeCancellation: 'IconThumbsUp',
  AccoCampaigns: 'IconCheckmark',
  CarRentalIncluded: 'IconCarFront',
  DownPayment: 'IconDeposit',
  HandLuggageIncluded: 'IconLuggageCheck',
  TransferIncluded: 'IconBusRight',
}

const CollapsibleHeader = styled.div`
  display: flex;
  justify-content: space-between;

  cursor: pointer;

  ${Typography} {
    ${Typography.modVariant('body-md-bold')}
  }

  ${Icon} {
    margin-left: ${tk('spacing_sm')};
  }
`
const Section = styled(CollapsibleRoot)`
  padding: ${tk('spacing_lg')} ${tk('spacing_md')};
  &:nth-of-type(2) {
    background-color: ${tk('color_semantic_bg_subdued')};
  }

  &:first-of-type {
    border-top: 2px solid ${tk('color_semantic_border_subdued')};
  }
`

const Content = styled(SpaceBetween)`
  padding: ${tk('spacing_lg')} ${tk('spacing_md')};
  padding-bottom: 0;
`

const Trailing = styled.div`
  display: flex;
`

const TextLine = styled.div`
  display: flex;
  justify-content: space-between;
`

const GrossPrice = styled.div`
  display: flex;
  justify-content: flex-end;
  color: ${tk('color_semantic_text_subdued')};
  text-decoration: line-through;
`

const SubtotalsWrapper = styled.div`
  padding: ${tk('spacing_lg')} ${tk('spacing_md')};
`

const Divider = styled.div`
  width: 100%;
  border-top: 2px solid ${tk('color_semantic_border_subdued')};
  margin-top: ${tk('spacing_sm')};
  margin-bottom: ${tk('spacing_sm')};
`

const BookButton = styled(BookNowButton)`
  width: 100%;
`
const FooterWrapper = styled(SpaceBetween)`
  padding-left: ${tk('spacing_md')};
  padding-right: ${tk('spacing_md')};
`

const ExtraCostsWrapper = styled.div`
  margin-top: ${tk('spacing_lg')};
`

const UspsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media ${tk('mq_tablet')} {
    flex-direction: row;
  }
`

export function PriceBreakdown() {
  const intl = useIntl()
  const { priceBreakdown, paymentMethod, transportType } = usePricesSelector(transportResponseSelector)
  const [reservationCostsOpen, setReservationCostsOpen] = React.useState(true)
  const [destinationCostsOpen, setDestinationCostsOpen] = React.useState(true)

  if (!priceBreakdown)
    return null

  const hasDiscount = priceBreakdown.totalGrossPrice > priceBreakdown.totalPrice

  return (
    <>
      <Section onOpenChange={setReservationCostsOpen} open={reservationCostsOpen}>
        <CollapsibleTrigger asChild>
          <CollapsibleHeader>
            <Typography>{intl.formatMessage({ id: 'price_basket_price_breakdown_pay_reservation' })}</Typography>
            <Trailing>
              {priceBreakdown.atReservation.subtotal > 0 && (
                <Typography>{intl.formatCurrency(priceBreakdown.atReservation.subtotal)}</Typography>
              )}
              <Icon glyph={reservationCostsOpen ? 'IconChevronUpSmall' : 'IconChevronDownSmall'} size="md" />
            </Trailing>
          </CollapsibleHeader>
        </CollapsibleTrigger>
        <Collapsible>
          <Content direction="y" spacing={tk('spacing_md')}>
            {priceBreakdown.atReservation.rooms.map((item, index) => (
              <RoomItem key={index} transportType={transportType} id={index + 1} items={item} />
            ))}
            {priceBreakdown.atReservation.services.map((item, index) => (
              <PriceBreakdownItem key={index} {...item} variant={item.price >= 0 ? 'default' : 'discount'} />
            ))}
            {priceBreakdown.atReservation.subtotal > 0 && (
              <PriceBreakdownItem
                header={intl.formatMessage({ id: 'price_basket_price_breakdown_subtotal' })}
                price={priceBreakdown.atReservation.subtotal}
                variant="bold"
              />
            )}
          </Content>
        </Collapsible>
      </Section>
      {priceBreakdown.atDestination.subtotal > 0 && (
        <Section onOpenChange={setDestinationCostsOpen} open={destinationCostsOpen}>
          <CollapsibleTrigger asChild>
            <CollapsibleHeader>
              <Typography>{intl.formatMessage({ id: 'price_basket_price_breakdown_pay_destination' })}</Typography>
              <Trailing>
                <Typography>{intl.formatCurrency(priceBreakdown.atDestination.subtotal)}</Typography>
                <Icon glyph={destinationCostsOpen ? 'IconChevronUpSmall' : 'IconChevronDownSmall'} size="md" />
              </Trailing>
            </CollapsibleHeader>
          </CollapsibleTrigger>
          <Collapsible>
            <Content direction="y" spacing={tk('spacing_md')}>
              {priceBreakdown.atDestination.items.map((item, index) => (
                <PriceBreakdownItem key={index} {...item} variant={item.price ? 'default' : 'discount'} />
              ))}
              <PriceBreakdownItem
                header={intl.formatMessage({ id: 'price_basket_price_breakdown_subtotal' })}
                price={priceBreakdown.atDestination.subtotal}
                variant="bold"
              />
            </Content>
            {priceBreakdown.extraCostsText && (
              <ExtraCostsWrapper>
                <Banner $type="neutral-dark" $withoutBorder>
                  <SpaceBetween direction="y">
                    <Typography $variant="body-md-bold">{priceBreakdown.extraCostsText.title}</Typography>
                    <Typography>{priceBreakdown.extraCostsText.text}</Typography>
                  </SpaceBetween>
                </Banner>
              </ExtraCostsWrapper>
            )}
          </Collapsible>
        </Section>
      )}
      <SubtotalsWrapper>
        {hasDiscount && (
          <GrossPrice>
            <Typography>{intl.formatCurrency(priceBreakdown.totalGrossPrice)}</Typography>
          </GrossPrice>
        )}
        <TextLine>
          <Typography $variant="body-lg-bold">
            {intl.formatMessage({ id: 'price_basket_price_breakdown_total' })}
          </Typography>
          <Typography $variant="body-lg-bold">{intl.formatCurrency(priceBreakdown.totalPrice)}</Typography>
        </TextLine>
        <TextLine>
          <Typography>{intl.formatMessage({ id: 'price_basket_price_breakdown_price_per_person' })}</Typography>
          <Typography>{intl.formatCurrency(priceBreakdown.pricePerPerson)}</Typography>
        </TextLine>
        <Divider />
        {priceBreakdown.atReservation.subtotal > 0 && (
          <TextLine>
            <Typography>{intl.formatMessage({ id: 'price_basket_price_breakdown_pay_reservation' })}</Typography>
            <Typography>{intl.formatCurrency(priceBreakdown.atReservation.subtotal)}</Typography>
          </TextLine>
        )}
        {priceBreakdown.atDestination.subtotal > 0 && (
          <TextLine>
            <Typography>{intl.formatMessage({ id: 'price_basket_price_breakdown_pay_destination' })}</Typography>
            <Typography>{intl.formatCurrency(priceBreakdown.atDestination.subtotal)}</Typography>
          </TextLine>
        )}
      </SubtotalsWrapper>
      <FooterWrapper direction="y" spacing={tk('spacing_lg')}>
        <BookButton />
        {paymentMethod?.text && paymentMethod?.icon && (<HighlightedPaymentMethod text={paymentMethod?.text} icon={paymentMethod?.icon} />)}
        <Banner $type="info">
          <Icon glyph="IconCarFront" size="md" />
          <Typography>{intl.formatMessage({ id: 'price_basket_price_breakdown_extra_costs_message' })}</Typography>
        </Banner>
        <UspsWrapper>
          {priceBreakdown.usps.map((usp, index) => (
            <Usp key={index} {...usp} />
          ))}
        </UspsWrapper>
      </FooterWrapper>
    </>
  )
}

const StrongText = styled(Typography)`
  color: ${tk('color_semantic_text_strong')};
`

const Wrapper = styled.div<{ $variant?: 'bold' | 'discount' | 'default' }>`
  ${({ $variant }) =>
    $variant === 'bold'
    && css`
      ${Typography} {
        ${Typography.modVariant('body-md-bold')}
      }
    `}
  ${({ $variant }) =>
    $variant === 'discount'
    && css`
      ${StrongText}, ${Typography} {
        color: ${tk('color_semantic_icon_success')};
      }
    `}
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
`

const AgeProfileWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

interface RoomItemProps {
  id: number
  transportType?: string
  items: {
    price: number
    valueText: string
    description: string
    isDiscount: boolean
  }[]
}
function RoomItem({ id, items, transportType }: RoomItemProps) {
  const intl = useIntl()
  const header = intl.formatMessage({ id: `prices_room_selector_room_group-title` }, { roomNumber: id })
  const getTransportTypeText = useMemo(() => {
    if (transportType === 'Flight') {
      return ` + ${intl.formatMessage({ id: 'prices_room_selector_room_group-transport-flight' })}`
    }
    if (transportType === 'Bus') {
      return ` + ${intl.formatMessage({ id: 'prices_room_selector_room_group-transport-bus' })}`
    }
    return ''
  }, [intl, transportType])
  return (
    <Wrapper>
      <Header>
        <Typography>
          {header}
          {getTransportTypeText}
        </Typography>
        <StrongText>{intl.formatCurrency(items.reduce((acc, item) => acc + item.price, 0))}</StrongText>
      </Header>
      {items.map((item, index) => (
        <AgeProfileWrapper key={index}>
          <StrongText>{item.valueText}</StrongText>
        </AgeProfileWrapper>
      ))}
    </Wrapper>
  )
}
interface PriceBreakdownItemProps {
  header: string
  price: number
  valueText?: string
  description?: string
  variant?: 'bold' | 'discount' | 'default'
}

function PriceBreakdownItem({ header, price, valueText, description, variant }: PriceBreakdownItemProps) {
  const intl = useIntl()
  const trailing = price ? `${intl.formatCurrency(price, variant === 'discount')}` : valueText

  return (
    <Wrapper $variant={variant}>
      <Header>
        <Typography>{header}</Typography>
        <StrongText>{trailing}</StrongText>
      </Header>
      {description && <StrongText>{description}</StrongText>}
    </Wrapper>
  )
}

const StyledUsp = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: ${tk('spacing_md')};

  @media ${tk('mq_tablet')} {
    flex-direction: column;
    flex: 1;
    text-align: center;
  }

  ${Icon} {
    color: ${tk('color_semantic_icon_strong')};
    margin-right: ${tk('spacing_xs')};

    @media ${tk('mq_tablet')} {
      margin-right: 0;
    }
  }

  ${Typography} {
    &:nth-of-type(3) {
      color: ${tk('color_semantic_text_strong')};
    }
  }
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

function Usp({ type, title, text }: { type: string, title: string, text?: string | undefined }) {
  return (
    <StyledUsp>
      <Icon glyph={PACKAGE_USP[type]} size="lg" />
      <TextWrapper>
        <Typography $variant="body-md-bold">{title}</Typography>
        {text && <Typography $variant="body-md">{text}</Typography>}
      </TextWrapper>
    </StyledUsp>
  )
}
